import { type FocusEventHandler, type ReactNode } from 'react'
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form'

import MoneyField from 'modules/core/components/MoneyField'

import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'

type Formatter<T = string> = {
  format: (value: T) => string
  parse: (value: string) => T | null
}

const defaultFormatter: Formatter = {
  format: (value) => value,
  parse: (value) => value,
}

type MoneyFieldProps<TFieldValues extends FieldValues> = Pick<
  UseControllerProps<TFieldValues>,
  'control' | 'name'
> & {
  placeholder?: string
  currencySymbol?: string
  completed?: boolean
  readonly?: boolean
  mandatory?: boolean
  label?: string
  secondaryLabel?: ReactNode | ReactNode[]
  formatter?: Formatter<number>
}

function MoneyInputField<TFieldValues extends FieldValues>(props: MoneyFieldProps<TFieldValues>) {
  const {
    control,
    name,
    placeholder,
    currencySymbol,
    readonly,
    mandatory,
    label,
    secondaryLabel,
    formatter = defaultFormatter,
    ...restProps
  } = props

  const { field } = useController({
    control,
    name,
    rules: {
      required: mandatory,
    },
  })

  const [currency] = useCurrencyContext()

  const onChange = (value: string) => {
    if (value === '') {
      field.onChange(null)
    } else {
      field.onChange(formatter.parse(value))
    }
  }

  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    field.onBlur()
  }

  const inputValue =
    field.value !== undefined && field.value !== null ? formatter.format(field.value) : ''

  return (
    <MoneyField
      {...restProps}
      value={inputValue}
      onChange={onChange}
      onBlur={onBlur}
      label={label}
      currency={currencySymbol || currency.isoCode}
      mandatory={mandatory}
      readonly={readonly}
      placeholder={placeholder}
    />
  )
}

export { MoneyInputField }
export type { Formatter }
